/**
 * The Music DNA analysis dashboard: six metric tiles plus the instruments and
 * atmosphere card.
 *
 * The Energy tile is written out rather than fed through Tile because it carries a
 * value on the label row and a ProgressBar underneath, which no other tile does.
 *
 * SIMULATED: every figure is hashed from the pasted URL string. Nothing is fetched
 * from Spotify and no audio is analysed — see src/lib/music.ts. This component does not
 * restate that, because it is a fact about the analysis and not about these tiles.
 */
import { Zap } from 'lucide-react';
import { ProgressBar } from '../ui';
import { c } from '../../styles/theme';
import type { MusicDNA } from '../../types/domain';

/** One metric tile. */
function Tile({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        border: `1px solid ${c.border}`,
        borderRadius: 10,
        padding: '10px 12px',
        background: c.surface1,
      }}
    >
      {/* The cell's label: the only thing naming the value beneath it. text2. */}
      <p
        style={{
          fontSize: 10,
          color: c.text2,
          margin: '0 0 3px',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        {label}
      </p>
      {children}
    </div>
  );
}

/** The value line shared by most tiles. `mono` for numbers, per the prototype. */
function TileValue({ mono, children }: { mono?: boolean; children: React.ReactNode }) {
  return (
    <p
      style={{
        fontSize: 13,
        fontWeight: 500,
        margin: 0,
        ...(mono ? { fontFamily: 'IBM Plex Mono' } : null),
      }}
    >
      {children}
    </p>
  );
}

export interface DnaAnalysisDashboardProps {
  dna: MusicDNA;
}

export default function DnaAnalysisDashboard({ dna }: DnaAnalysisDashboardProps) {
  return (
    <>
      <p style={{ fontSize: 12, color: c.text2, margin: '0 0 8px' }}>Analysis dashboard</p>
      <div className="lyx-grid-2" style={{ marginBottom: 18 }}>
        <Tile label="Genre">
          <TileValue>{dna.genre}</TileValue>
        </Tile>
        <Tile label="Mood">
          <TileValue>{dna.mood}</TileValue>
        </Tile>
        <div
          style={{
            border: `1px solid ${c.border}`,
            borderRadius: 10,
            padding: '10px 12px',
            background: c.surface1,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 5,
            }}
          >
            <p
              style={{
                fontSize: 10,
                color: c.text2,
                margin: 0,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
              }}
            >
              <Zap size={10} />
              Energy
            </p>
            <span style={{ fontSize: 11, color: c.text2, fontFamily: 'IBM Plex Mono' }}>
              {dna.energy}%
            </span>
          </div>
          <ProgressBar value={dna.energy} max={100} height={4} label="Musical energy" />
        </div>
        <Tile label="Tempo · BPM">
          <TileValue mono>{dna.bpm} BPM</TileValue>
        </Tile>
        <Tile label="Vocal style">
          <TileValue>{dna.vocals}</TileValue>
        </Tile>
        <Tile label="Production style">
          <TileValue>{dna.production}</TileValue>
        </Tile>
      </div>

      <div
        style={{
          border: `1px solid ${c.border}`,
          borderRadius: 10,
          padding: '12px 14px',
          background: c.surface1,
          marginBottom: 20,
        }}
      >
        <p
          style={{
            fontSize: 10,
            color: c.text2,
            margin: '0 0 8px',
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}
        >
          Music DNA · instruments &amp; atmosphere
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {dna.instruments.map((inst) => (
            <span
              key={inst}
              style={{
                fontSize: 11,
                padding: '3px 9px',
                borderRadius: 999,
                background: c.surface3,
                color: c.text2,
              }}
            >
              {inst}
            </span>
          ))}
        </div>
        <p style={{ fontSize: 12, color: c.text2, margin: 0 }}>Atmosphere: {dna.atmosphere}</p>
      </div>
    </>
  );
}
